import type { TaxEntity } from "../../domain/entities/TaxEntity";
import type { ChangeDetails } from "../../utils/reviewWorkflow";
import { buildInvoiceBillDisplayDiff } from "./buildInvoiceBillDisplayDiff";
import type {
  DisplayDiffItem,
  InvoiceOrBillKind,
} from "./invoiceBillDisplayDiff.types";
import type {
  InvoiceBillHeaderLike,
  InvoiceBillLineLike,
} from "./invoiceBillDisplayValues";
import { isBillTrackedFieldChangeRowAllowed } from "./billPendingReviewDisplayMaps";
import { isInvoiceTrackedFieldChangeRowAllowed } from "./invoicePendingReviewDisplayMaps";

function hasSnapshotPair(
  changeDetails: ChangeDetails | null | undefined,
): boolean {
  const o = changeDetails?.original_json;
  const r = changeDetails?.raw_json;
  return (
    o != null && r != null && typeof o === "object" && typeof r === "object"
  );
}

function displayDiffItemToLine(item: DisplayDiffItem): string {
  const oldT = String(item.oldText ?? "").trim();
  const newT = String(item.newText ?? "").trim();
  const prefix =
    item.scope === "line" && item.lineNumber != null
      ? `Line ${item.lineNumber} — ${item.label}`
      : item.label;
  if (!oldT || oldT === "—") {
    return `${prefix}: ${newT}`;
  }
  return `${prefix}: ${oldT} → ${newT}`;
}

const SYSTEM_LINE_FIELD_KEYS = new Set([
  "account_name",
  "tax_name",
  "tax_percentage",
  "tax_amount",
  "tds_tax_name",
  "tds_tax_percentage",
  "item_total",
  "item_total_inclusive_of_tax",
]);

function fieldChangeMatchesDisplayDiffItem(
  fc: { field: string },
  item: DisplayDiffItem,
): boolean {
  const field = String(fc.field || "").trim().toLowerCase();
  if (!field) return false;
  if (item.scope === "line" && item.lineNumber != null) {
    return (
      field ===
      `line item ${item.lineNumber}: ${String(item.label || "")
        .trim()
        .toLowerCase()}`
    );
  }
  return field === String(item.label || "").trim().toLowerCase();
}

/**
 * System-applied invoice/bill changes are computed from immutable original JSON
 * to the validation baseline. User-requested changes are still computed from the
 * validation baseline to raw_json by `formatInvoiceBillChangeSummaryLines`.
 */
export function formatInvoiceBillSystemChangeSummaryLines(
  kind: InvoiceOrBillKind,
  changeDetails: ChangeDetails | null | undefined,
  taxes?: TaxEntity[] | undefined,
): string[] {
  if (!changeDetails || !hasSnapshotPair(changeDetails)) return [];
  const snapshotLines = changeDetails.validation_snapshot?.line_items;
  if (!Array.isArray(snapshotLines) || snapshotLines.length === 0) return [];

  const orig = changeDetails.original_json as InvoiceBillHeaderLike & {
    line_items?: InvoiceBillLineLike[];
  };
  const fieldChanges = changeDetails.field_changes ?? [];
  const items = buildInvoiceBillDisplayDiff({
    kind,
    originalHeader: orig,
    newHeader: orig,
    originalLineItems: orig?.line_items,
    newLineItems: snapshotLines as InvoiceBillLineLike[],
    taxes,
  });

  return items
    .filter(
      (item) =>
        item.scope === "line" &&
        SYSTEM_LINE_FIELD_KEYS.has(item.fieldKey) &&
        !fieldChanges.some((fc) => fieldChangeMatchesDisplayDiffItem(fc, item)),
    )
    .map(displayDiffItemToLine);
}

export function formatInvoiceBillReviewSummarySections(
  kind: InvoiceOrBillKind,
  changeDetails: ChangeDetails | null | undefined,
  taxes?: TaxEntity[] | undefined,
): {
  systemChanges: string[];
  userChanges: string[];
} {
  return {
    systemChanges: formatInvoiceBillSystemChangeSummaryLines(
      kind,
      changeDetails,
      taxes,
    ),
    userChanges: formatInvoiceBillChangeSummaryLines(
      kind,
      changeDetails,
      taxes,
    ),
  };
}

/**
 * When JSON snapshot diff and `field_changes` are merged, the same party rename can appear twice:
 * snapshot `Customer Name: A → B` plus FC `Customer: B` (or the same for bills with Vendor).
 */
function dropFcLinesRedundantWithPartyNameSnapshot(
  kind: InvoiceOrBillKind,
  snapshotLines: string[],
  fcLines: string[],
): string[] {
  if (snapshotLines.length === 0 || fcLines.length === 0) return fcLines;
  const trimSnap = new Set(snapshotLines.map((l) => l.trim()));

  const hasInvoiceCustomerName = snapshotLines.some((l) =>
    /^Customer Name:/i.test(l.trim()),
  );
  const hasBillVendorName =
    kind === "bill" &&
    snapshotLines.some((l) => /^Vendor Name:/i.test(l.trim()));

  return fcLines.filter((raw) => {
    const line = raw.trim();
    if (trimSnap.has(line)) return false;

    if (kind === "invoice" && hasInvoiceCustomerName) {
      if (/^Customer:\s/i.test(line) && !line.includes("→")) return false;
      if (/^Customer has changed from\b/i.test(line)) return false;
      if (/^Customer Name:\s/i.test(line) && line.includes("→")) return false;
    }
    if (kind === "bill" && hasBillVendorName) {
      if (/^Vendor:\s/i.test(line) && !line.includes("→")) return false;
      if (/^Vendor has changed from\b/i.test(line)) return false;
      if (/^Vendor Name:\s/i.test(line) && line.includes("→")) return false;
    }
    return true;
  });
}

// --- Shared with ApprovalDetailPanel / ApprovalCard (field_changes fallback) ---

const convertDateFormat = (dateStr: string): string => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (dateRegex.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }
  return dateStr;
};

const formatValue = (val: string): string => {
  if (!val || val === "empty") return val;
  const strVal = String(val);
  const convertedDate = convertDateFormat(strVal);
  if (convertedDate !== strVal) {
    return convertedDate;
  }
  if (
    strVal.includes("_") &&
    !strVal.startsWith("[") &&
    !strVal.startsWith("{")
  ) {
    return strVal
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  return strVal;
};

const formatLineItemValue = (val: string, fieldLabel: string): string => {
  const labelLower = String(fieldLabel || "").toLowerCase();
  const isAnyTaxName =
    labelLower.includes("tax name") || labelLower.includes("tax_name");
  const isTdsTaxName = labelLower.includes("tds") && isAnyTaxName;
  const isGstTaxName = isAnyTaxName && !isTdsTaxName;

  // GST tax_name: empty should display as "No Tax"
  if (isGstTaxName) {
    if (!val || val === "empty" || String(val).trim() === "") return "No Tax";
    return String(val).toUpperCase();
  }

  // TDS tax name: never coerce empty → "No Tax"; keep empty semantics, uppercase when present
  if (isTdsTaxName) {
    if (!val || val === "empty" || String(val).trim() === "") return "empty";
    return String(val).toUpperCase();
  }
  return formatValue(val);
};

/**
 * Allowlist-filtered field_changes lines for invoice (matches legacy ApprovalCard ordering).
 */
export function formatInvoiceFieldChangesForApprovalDisplay(
  fieldChanges: Array<{ field: string; old: string; new: string }>,
): string[] {
  const customerChanges: string[] = [];
  const otherChanges: string[] = [];
  const lineItemChangesByNum = new Map<
    number,
    Array<{ index: number; text: string }>
  >();
  const idFieldsToHide = [
    "customer_id",
    "vendor_id",
    "tax_id",
    "account_id",
    "gl_id",
    "tds_tax_id",
    "id",
    "_id",
  ];

  let customerChanged = false;

  fieldChanges.forEach((fc, index) => {
    const fieldLower = fc.field.toLowerCase();
    const formattedOld = formatValue(fc.old);
    const formattedNew = formatValue(fc.new);

    if (formattedOld === formattedNew) return;
    if (fc.field.startsWith("line_items[") && fc.field.endsWith("]")) return;

    if (
      idFieldsToHide.some((idField) => fieldLower.includes(idField)) ||
      fieldLower.endsWith("_id") ||
      fieldLower.endsWith("id")
    ) {
      return;
    }

    if (fc.field.includes("_") && !fc.field.startsWith("Line Item ")) {
      return;
    }

    if (
      fieldLower === "customer" ||
      fieldLower === "customer_name" ||
      fieldLower === "customer name"
    ) {
      if (!customerChanged && formattedOld !== formattedNew) {
        customerChanged = true;
        let displayNew = formattedNew;
        if (displayNew.includes("Created a new customer with the name:")) {
          displayNew = displayNew
            .replace("Created a new customer with the name:", "")
            .trim();
        }
        if (formattedOld === "empty" || !formattedOld) {
          customerChanges.push(`Customer: ${displayNew}`);
        } else {
          customerChanges.push(
            `Customer has changed from ${formattedOld} → ${displayNew}`,
          );
        }
      }
      return;
    }

    const isCustomerGstField =
      fieldLower.includes("customer") &&
      (fieldLower.includes("gst") || fieldLower.includes("gstin"));
    const isGstNoField =
      (fieldLower === "gst_no" ||
        fieldLower === "gst no" ||
        (fieldLower.includes("gst") && fieldLower.includes("no"))) &&
      !fieldLower.includes("vendor");

    if (isCustomerGstField || isGstNoField) {
      customerChanges.push(
        `Customer GST changed from ${formattedOld} → ${formattedNew}`,
      );
      return;
    }

    if (fc.field.startsWith("Line Item ")) {
      const match = fc.field.match(/^Line Item (\d+)(?::\s*(.*))?$/i);
      const lineNum = match ? parseInt(match[1], 10) : 0;
      const subfield = match?.[2]?.trim();
      const lineFormattedOld = formatLineItemValue(fc.old, fc.field);
      const lineFormattedNew = formatLineItemValue(fc.new, fc.field);
      const text =
        subfield != null && subfield !== ""
          ? lineFormattedOld === "empty" || !lineFormattedOld
            ? `  ${subfield}: ${lineFormattedNew}`
            : `  ${subfield}: ${lineFormattedOld} → ${lineFormattedNew}`
          : lineFormattedNew === "Removed"
            ? "  Removed"
            : `  ${lineFormattedNew}`;
      if (!lineItemChangesByNum.has(lineNum)) {
        lineItemChangesByNum.set(lineNum, []);
      }
      lineItemChangesByNum.get(lineNum)!.push({ index, text });
      return;
    }

    let displayField = fc.field;
    if (fieldLower === "gst no" || fieldLower === "gst_no") {
      displayField = "Customer GST";
    }

    if (fieldLower === "payment terms" || fieldLower === "payment_terms") {
      return;
    }
    if (
      fieldLower === "gst category" ||
      fieldLower === "gst_category" ||
      fieldLower === "source of supply" ||
      fieldLower === "source_of_supply"
    ) {
      return;
    }

    const isCustomerRelatedField =
      fieldLower.includes("customer") ||
      fieldLower.includes("gst") ||
      fieldLower.includes("gstin");

    const changeText =
      formattedOld === "empty" || !formattedOld
        ? `${displayField}: ${formattedNew}`
        : `${displayField}: ${formattedOld} → ${formattedNew}`;
    if (isCustomerRelatedField) {
      customerChanges.push(changeText);
    } else {
      otherChanges.push(changeText);
    }
  });

  const sortedCustomerChanges = customerChanges.sort((a, b) => {
    if (a.startsWith("Customer has changed")) return -1;
    if (b.startsWith("Customer has changed")) return 1;
    if (a.startsWith("Customer GST changed")) return -1;
    if (b.startsWith("Customer GST changed")) return 1;
    return 0;
  });

  const lineItemLines: string[] = [];
  const lineNumbers = Array.from(lineItemChangesByNum.keys()).sort(
    (a, b) => a - b,
  );
  for (const lineNum of lineNumbers) {
    const entries = lineItemChangesByNum.get(lineNum)!;
    entries.sort((a, b) => a.index - b.index);
    lineItemLines.push(`Line Item ${lineNum}:`);
    lineItemLines.push(...entries.map((e) => e.text));
  }

  return [...sortedCustomerChanges, ...otherChanges, ...lineItemLines];
}

/**
 * Allowlist-filtered field_changes lines for bill.
 */
export function formatBillFieldChangesForApprovalDisplay(
  fieldChanges: Array<{ field: string; old: string; new: string }>,
): string[] {
  const otherChanges: string[] = [];
  const lineItemChangesByNum = new Map<
    number,
    Array<{ index: number; text: string }>
  >();
  const idFieldsToHide = [
    "customer_id",
    "vendor_id",
    "tax_id",
    "account_id",
    "gl_id",
    "tds_tax_id",
    "id",
    "_id",
  ];

  let vendorChanged = false;

  fieldChanges.forEach((fc, index) => {
    const fieldLower = fc.field.toLowerCase();
    const formattedOld = formatValue(fc.old);
    const formattedNew = formatValue(fc.new);

    if (formattedOld === formattedNew) return;
    if (fc.field.startsWith("line_items[") && fc.field.endsWith("]")) return;

    if (
      idFieldsToHide.some((idField) => fieldLower.includes(idField)) ||
      fieldLower.endsWith("_id") ||
      fieldLower.endsWith("id")
    ) {
      return;
    }

    if (fc.field.includes("_") && !fc.field.startsWith("Line Item ")) {
      return;
    }

    if (
      fieldLower === "vendor" ||
      fieldLower === "vendor_name" ||
      fieldLower === "vendor name"
    ) {
      if (!vendorChanged && formattedOld !== formattedNew) {
        vendorChanged = true;
        let displayNew = formattedNew;
        if (displayNew.includes("Created a new vendor with the name:")) {
          displayNew = displayNew
            .replace("Created a new vendor with the name:", "")
            .trim();
        }
        if (formattedOld === "empty" || !formattedOld) {
          otherChanges.push(`Vendor: ${displayNew}`);
        } else {
          otherChanges.push(
            `Vendor has changed from ${formattedOld} → ${displayNew}`,
          );
        }
      }
      return;
    }

    const isVendorGstField =
      fieldLower.includes("vendor") &&
      (fieldLower.includes("gst") || fieldLower.includes("gstin"));
    const isGstNoField =
      (fieldLower === "gst_no" ||
        fieldLower === "gst no" ||
        (fieldLower.includes("gst") && fieldLower.includes("no"))) &&
      !fieldLower.includes("customer");

    if (isVendorGstField || isGstNoField) {
      otherChanges.push(
        `Vendor GST changed from ${formattedOld} → ${formattedNew}`,
      );
      return;
    }

    if (fc.field.startsWith("Line Item ")) {
      const match = fc.field.match(/^Line Item (\d+)(?::\s*(.*))?$/i);
      const lineNum = match ? parseInt(match[1], 10) : 0;
      const subfield = match?.[2]?.trim();
      const lineFormattedOld = formatLineItemValue(fc.old, fc.field);
      const lineFormattedNew = formatLineItemValue(fc.new, fc.field);
      const text =
        subfield != null && subfield !== ""
          ? lineFormattedOld === "empty" || !lineFormattedOld
            ? `  ${subfield}: ${lineFormattedNew}`
            : `  ${subfield}: ${lineFormattedOld} → ${lineFormattedNew}`
          : lineFormattedNew === "Removed"
            ? "  Removed"
            : `  ${lineFormattedNew}`;
      if (!lineItemChangesByNum.has(lineNum)) {
        lineItemChangesByNum.set(lineNum, []);
      }
      lineItemChangesByNum.get(lineNum)!.push({ index, text });
      return;
    }

    let displayField = fc.field;
    if (fieldLower === "gst no" || fieldLower === "gst_no") {
      displayField = "Vendor GST";
    }

    if (fieldLower === "payment terms" || fieldLower === "payment_terms") {
      return;
    }
    if (
      fieldLower === "gst category" ||
      fieldLower === "gst_category" ||
      fieldLower === "source of supply" ||
      fieldLower === "source_of_supply"
    ) {
      return;
    }

    const changeText =
      formattedOld === "empty" || !formattedOld
        ? `${displayField}: ${formattedNew}`
        : `${displayField}: ${formattedOld} → ${formattedNew}`;
    otherChanges.push(changeText);
  });

  const lineItemLines: string[] = [];
  const lineNumbers = Array.from(lineItemChangesByNum.keys()).sort(
    (a, b) => a - b,
  );
  for (const lineNum of lineNumbers) {
    const entries = lineItemChangesByNum.get(lineNum)!;
    entries.sort((a, b) => a.index - b.index);
    lineItemLines.push(`Line Item ${lineNum}:`);
    lineItemLines.push(...entries.map((e) => e.text));
  }

  return [...otherChanges, ...lineItemLines];
}

/**
 * Field-changes only (no JSON snapshot), after allowlist — for GL override "other" section.
 */
export function formatInvoiceBillFieldChangesForApprovalList(
  kind: InvoiceOrBillKind,
  fieldChanges: Array<{ field: string; old: string; new: string }>,
): string[] {
  const allow =
    kind === "invoice"
      ? isInvoiceTrackedFieldChangeRowAllowed
      : isBillTrackedFieldChangeRowAllowed;
  const filtered = fieldChanges.filter((c) => allow(c.field));
  return kind === "invoice"
    ? formatInvoiceFieldChangesForApprovalDisplay(filtered)
    : formatBillFieldChangesForApprovalDisplay(filtered);
}

/**
 * Primary summary lines for invoice/bill approvals and review UIs.
 * When `original_json` + `raw_json` exist, builds an allowlisted snapshot diff; when `field_changes`
 * are also present, merges both so tracked line/header edits are not dropped when the snapshot
 * already shows other rows (e.g. header totals).
 */
export function formatInvoiceBillChangeSummaryLines(
  kind: InvoiceOrBillKind,
  changeDetails: ChangeDetails | null | undefined,
  taxes?: TaxEntity[] | undefined,
): string[] {
  if (!changeDetails) return [];

  let snapshotLines: string[] = [];

  if (hasSnapshotPair(changeDetails)) {
    const orig = changeDetails.original_json as InvoiceBillHeaderLike & {
      line_items?: InvoiceBillLineLike[];
    };
    const next = changeDetails.raw_json as InvoiceBillHeaderLike & {
      line_items?: InvoiceBillLineLike[];
    };
    const vs = changeDetails.validation_snapshot;
    const validationSnapshot =
      vs && Array.isArray(vs.line_items) && vs.line_items.length > 0
        ? {
            line_items: vs.line_items as
              | InvoiceBillLineLike[]
              | null
              | undefined,
            tax_total: vs.tax_total,
            sub_total: vs.sub_total,
            total: vs.total,
          }
        : undefined;

    const items = buildInvoiceBillDisplayDiff({
      kind,
      originalHeader: orig,
      newHeader: next,
      originalLineItems: orig?.line_items,
      newLineItems: next?.line_items,
      taxes,
      validationSnapshot: validationSnapshot ?? null,
      omitValidationOwnedLineFields: true,
    });
    snapshotLines = items.map(displayDiffItemToLine);
  }

  const fc = changeDetails.field_changes;
  const fcLines =
    fc && fc.length > 0
      ? formatInvoiceBillFieldChangesForApprovalList(kind, fc)
      : [];

  if (snapshotLines.length > 0 && fcLines.length > 0) {
    // If we already have allowlisted `field_changes` line-item entries, hide the line-scoped
    // snapshot rows (e.g. "Line 1 — Account Name...") to avoid duplicate line-item summaries.
    const hasLineItemFieldChanges = (fc ?? []).some((c) =>
      /^Line Item\s+\d+/i.test(String(c.field || "").trim()),
    );
    if (hasLineItemFieldChanges) {
      snapshotLines = snapshotLines.filter(
        (l) => !/^Line\s+\d+\s+—/i.test(String(l).trim()),
      );
    }
    const fcDeduped = dropFcLinesRedundantWithPartyNameSnapshot(
      kind,
      snapshotLines,
      fcLines,
    );
    return [...snapshotLines, "", ...fcDeduped];
  }
  if (snapshotLines.length > 0) {
    return snapshotLines;
  }
  if (fcLines.length > 0) {
    return fcLines;
  }

  return [];
}
